import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  IDeviceShadowRecord,
  IDeviceShadowValue,
  IRangeDate,
  IRangeNumber,
  IShadowQueryResponse,
  ShadowQueryRequest,
} from '../device-shadow/device-shadow.service';

// Service responsible for fetching battery-health data from the deviceshadow API.
// It reuses the /deviceShadow POST endpoint, but exposes a battery-shaped request
// surface and a battery-shaped row type so the Insights / Device Battery tab can
// stay decoupled from the wider DeviceShadow model.
@Injectable({
  providedIn: 'root',
})
export class DeviceBatteryService {
  private readonly url = environment.deviceShadow;

  constructor(private readonly httpClient: HttpClient) {}

  async getDeviceBatteries(
    request: DeviceBatteryQueryRequest,
  ): Promise<IDeviceBatteryQueryResponse> {
    const response = await this.httpClient
      .post<IShadowQueryResponse>(`${this.url}/deviceShadow`, request)
      .toPromise();

    return {
      results: (response?.results ?? []).map(toBatteryRecord),
      total: response?.total ?? 0,
      scrollId: response?.scrollId,
    };
  }

  deleteScrollId(scrollId: string): Promise<void> {
    return this.httpClient
      .delete<void>(
        `${this.url}/deviceShadow?scrollId=${encodeURIComponent(scrollId)}`,
      )
      .toPromise();
  }
}

// Battery health values come back from the API as the raw enum int (0/1/2/3)
// stored on BatteryIssueBatteryHealth, plus a derived display string on
// BatteryHealth. The DeviceShadow model already produces the friendly string
// (Good / Weak / Critical / Not Charging) so the UI just maps it once more
// to the Figma copy (Normal / Weak / Faulty / Not Charging).
export enum BatteryHealthRaw {
  Good = 0,
  Weak = 1,
  Critical = 2,
  NotCharging = 3,
  Unknown = 4,
}

export const BATTERY_HEALTH_DISPLAY: Record<string, string> = {
  Good: 'Normal Battery',
  Weak: 'Weak Battery',
  Critical: 'Faulty Battery',
  'Not Charging': 'Not Charging',
  Unknown: 'Unknown',
};

// Filters available on the Device Battery tab. These map 1:1 to the
// ShadowQuery fields on the deviceshadow API; see
// Lytx.DeviceShadow.Api/Shadow/ShadowQuery.cs for the canonical list.
export interface DeviceBatteryQueryRequest extends ShadowQueryRequest {
  // Accepts either the display name (e.g. "Critical") or the raw int as a
  // string. The API does the normalisation server-side via BatteryHealthHelper.
  batteryHealth?: string;
  batteryIssueBatteryHealth?: IRangeNumber;
  batteryIssueReason?: string;
  batteryIssueIssueTimestamp?: IRangeDate;
  batteryIssueManufacturerName?: string;
  batteryIssueIsRechargeable?: string;

  // device.batterybackupstats — value/timestamp filters.
  batteryBackupStatsTimestamp?: IRangeDate;
  batteryBackupStatsTotalIdleDischargeTimeSeconds?: IRangeNumber;
  batteryBackupStatsLowVoltageRechargeAttempts?: IRangeNumber;
}

export interface IDeviceBatteryQueryResponse {
  results: IDeviceBatteryRecord[];
  total: number;
  scrollId?: string;
}

// The fields exposed to the Device Battery grid. Each one is a thin facade
// over the deviceshadow row, with a small amount of derivation (e.g. idle
// hours from seconds, high-cycling from recharge-attempt counts, action
// from battery-health). The recommended action is computed here rather than
// in the template so it stays consistent with CSV export when we add it.
export interface IDeviceBatteryRecord {
  serialNumber: string;
  companyName: string;
  companyId: number;
  batteryType: string;
  batteryHealth: string;
  batteryHealthDisplay: string;
  chargeStatus: string;
  avgVolt: number | null;
  idleHrs: number | null;
  highCycling: 'Yes' | 'No' | '';
  lastTelemetryRec: string | null;
  action: 'No Action' | 'Monitor' | 'Replace Device' | '';
  raw: IDeviceShadowRecord;
}

const HIGH_CYCLING_THRESHOLD = 5;

function toBatteryRecord(record: IDeviceShadowRecord): IDeviceBatteryRecord {
  const batteryHealth = (record as IDeviceShadowRecord & {
    batteryHealth?: string;
  }).batteryHealth ?? '';
  const batteryIssueManufacturerName = readShadowString(
    record,
    'batteryIssueManufacturerName',
  );
  const batteryBackupStatsTimestamp = (record as IDeviceShadowRecord & {
    batteryBackupStatsTimestamp?: string;
  }).batteryBackupStatsTimestamp;

  const totalChargeEnabled = readShadowNumber(
    record,
    'batteryBackupStatsTotalChargeEnabledSeconds',
  );
  const idleSeconds = readShadowNumber(
    record,
    'batteryBackupStatsTotalIdleDischargeTimeSeconds',
  );
  const lowVoltageRecharges = readShadowNumber(
    record,
    'batteryBackupStatsLowVoltageRechargeAttempts',
  );

  return {
    serialNumber: record.serialNumber,
    companyName: record.companyName,
    companyId: record.companyId,
    batteryType: batteryIssueManufacturerName || 'Other',
    batteryHealth,
    batteryHealthDisplay:
      BATTERY_HEALTH_DISPLAY[batteryHealth] ?? batteryHealth ?? '',
    chargeStatus: deriveChargeStatus(batteryHealth, totalChargeEnabled),
    avgVolt: readShadowNumber(record, 'batteryVoltage'),
    idleHrs: idleSeconds == null ? null : Math.round(idleSeconds / 3600),
    highCycling: deriveHighCycling(lowVoltageRecharges),
    lastTelemetryRec: batteryBackupStatsTimestamp ?? null,
    action: deriveAction(batteryHealth, deriveHighCycling(lowVoltageRecharges)),
    raw: record,
  };
}

function deriveChargeStatus(
  batteryHealth: string,
  totalChargeEnabled: number | null,
): string {
  if (batteryHealth === 'Good' && (totalChargeEnabled ?? 0) > 0) {
    return 'Good';
  }
  return 'Unknown';
}

function deriveHighCycling(
  lowVoltageRecharges: number | null,
): 'Yes' | 'No' | '' {
  if (lowVoltageRecharges == null) {
    return '';
  }
  return lowVoltageRecharges > HIGH_CYCLING_THRESHOLD ? 'Yes' : 'No';
}

function deriveAction(
  batteryHealth: string,
  highCycling: 'Yes' | 'No' | '',
): IDeviceBatteryRecord['action'] {
  switch (batteryHealth) {
    case 'Critical':
    case 'Not Charging':
    case 'Weak':
      return 'Replace Device';
    case 'Good':
      return highCycling === 'Yes' ? 'Monitor' : 'No Action';
    default:
      return '';
  }
}

function readShadowNumber(
  record: IDeviceShadowRecord,
  field: string,
): number | null {
  const value = (record as unknown as Record<string, IDeviceShadowValue>)[field];
  if (value == null) {
    return null;
  }
  const parsed = parseFloat(value.value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readShadowString(
  record: IDeviceShadowRecord,
  field: string,
): string | null {
  const value = (record as unknown as Record<string, IDeviceShadowValue>)[field];
  return value?.value ?? null;
}
